import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useSocket } from '@/contexts/SocketContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Heartbeat } from './Heartbeat';
import {
  HomeIcon, Dumbbell, Target, Users, User,
  Settings, LogOut, Menu, X, Bell, Activity, BookOpen,
  Shield, Brain, Coins, Zap, Watch, Sun, Moon, Calendar,
} from 'lucide-react';
import LoadingSpinner from './ui/LoadingSpinner';
import MobileNav from './MobileNav';

const NAV = [
  { name: 'Dashboard',     href: '/dashboard',    icon: HomeIcon,  group: 'main' },
  { name: 'Workouts',      href: '/workouts',     icon: Dumbbell,  group: 'main' },
  { name: 'Guides',        href: '/workout-guides', icon: BookOpen, group: 'main' },
  { name: 'Exercises',     href: '/exercises',    icon: Activity,  group: 'main' },
  { name: 'Goals',         href: '/goals',        icon: Target,    group: 'main' },
  { name: 'Plans',         href: '/schedules',    icon: Calendar,  group: 'main' },
  { name: 'Social',        href: '/social',       icon: Users,     group: 'community' },
  { name: 'Locker Room',   href: '/locker-room',  icon: Shield,    group: 'community' },
  { name: 'AI Coach',      href: '/ai-coach',     icon: Brain,     group: 'pro' },
  { name: 'Wearables',     href: '/wearables',    icon: Watch,     group: 'pro' },
  { name: 'FitCoin',       href: '/fitcoin',      icon: Coins,     group: 'pro' },
  { name: 'Vibe Sync',     href: '/vibe-sync',    icon: Zap,       group: 'pro' },
];

const GROUP_LABELS: Record<string, string> = {
  main: 'Training',
  community: 'Community',
  pro: 'Insights',
};

const Layout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const { user, logout, isLoading } = useAuth();
  const { notifications, unreadCount, markNotificationsRead, isConnected, clearNotifications } = useSocket();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try { await logout(); navigate('/login'); } catch (e) { console.error(e); }
  };

  if (isLoading) {
    return <div className="min-h-screen app-surface flex items-center justify-center"><LoadingSpinner size="lg" /></div>;
  }

  const groups = ['main', 'community', 'pro'];
  const pageTitle =
    NAV.find(n => location.pathname === n.href || location.pathname.startsWith(`${n.href}/`))?.name
    ?? (location.pathname.startsWith('/profile') ? 'Profile'
      : location.pathname.startsWith('/settings') ? 'Settings'
      : '');

  return (
    <div className="min-h-screen app-surface flex">
      {/* Visible only once focused — lets keyboard users jump the nav rail */}
      <a href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[100]
                   focus:px-4 focus:py-2 focus:rounded-xl focus:bg-accent focus:text-accent-ink focus:font-semibold">
        Skip to content
      </a>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 bg-ink/40 backdrop-blur-sm lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* ── Sidebar ─────────────────────────────────────────────────────────── */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-60 app-sidebar app-border border-r flex flex-col
        transform transition-transform duration-300 ease-in-out
        lg:translate-x-0 lg:relative lg:flex-shrink-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>

        {/* Logo */}
        <div className="flex items-center justify-between h-16 px-5 app-border border-b">
          <Link to="/dashboard" className="flex items-center gap-2.5" onClick={() => setSidebarOpen(false)}>
            <div className="p-1.5 rounded-lg bg-accent/15 border border-accent/20">
              <Dumbbell className="h-5 w-5 text-accent" />
            </div>
            <span className="text-base font-black text-ink tracking-tight">FitTrack</span>
          </Link>
          <button onClick={() => setSidebarOpen(false)} aria-label="Close navigation menu"
            className="lg:hidden p-1 rounded-lg text-ink-dim hover:text-ink-muted">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-4 overflow-y-auto space-y-5">
          {groups.map(group => {
            const items = NAV.filter(n => n.group === group);
            return (
              <div key={group}>
                <p className="text-[10px] font-bold text-ink-faint uppercase tracking-widest px-3 mb-1.5">
                  {GROUP_LABELS[group]}
                </p>
                <div className="space-y-0.5">
                  {items.map(item => {
                    const isActive =
                      location.pathname === item.href ||
                      location.pathname.startsWith(`${item.href}/`);
                    return (
                      <Link key={item.name} to={item.href} onClick={() => setSidebarOpen(false)}
                        aria-current={isActive ? 'page' : undefined}
                        className={`relative flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all duration-150
                          ${isActive
                            ? 'bg-accent/10 text-accent font-semibold'
                            : 'text-ink-dim hover:text-ink hover:bg-subtle font-medium'
                          }`}>
                        {isActive && (
                          <span aria-hidden className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-[3px] rounded-r-full bg-accent" />
                        )}
                        <item.icon className="h-4 w-4 flex-shrink-0" />
                        {item.name}
                      </Link>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </nav>

        {/* User footer */}
        <div className="app-border border-t p-3">
          <div className="flex items-center gap-3 p-2 rounded-xl hover:bg-subtle transition-colors mb-1">
            <div className="flex-shrink-0">
              {user?.profilePicture ? (
                <img className="h-8 w-8 rounded-full object-cover ring-1 ring-accent/30" src={user.profilePicture} alt="" />
              ) : (
                <div className="h-8 w-8 rounded-full bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center text-white font-bold text-xs">
                  {user?.firstName?.[0]}{user?.lastName?.[0]}
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-ink truncate">{user?.firstName} {user?.lastName}</p>
              <p className="text-xs text-ink-faint truncate">@{user?.username}</p>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-1">
            {[
              { to: '/profile',  icon: User,     label: 'Profile' },
              { to: '/settings', icon: Settings, label: 'Settings' },
            ].map(a => (
              <Link key={a.to} to={a.to} onClick={() => setSidebarOpen(false)}
                className="flex flex-col items-center gap-1 p-2 rounded-lg text-ink-faint hover:text-ink-muted hover:bg-subtle transition-all">
                <a.icon className="h-3.5 w-3.5" />
                <span className="text-[10px]">{a.label}</span>
              </Link>
            ))}
            <button onClick={handleLogout}
              className="flex flex-col items-center gap-1 p-2 rounded-lg text-ink-faint hover:text-red-400 hover:bg-red-500/10 transition-all">
              <LogOut className="h-3.5 w-3.5" />
              <span className="text-[10px]">Logout</span>
            </button>
          </div>
        </div>
      </aside>

      {/* ── Main ────────────────────────────────────────────────────────────── */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Top bar */}
        <header className="sticky top-0 z-30 app-header backdrop-blur-md app-border border-b px-4 py-3 sm:px-6">
          <div className="flex items-center justify-between">
            <button onClick={() => setSidebarOpen(true)} aria-label="Open navigation menu"
              className="lg:hidden p-2 rounded-xl text-ink-dim hover:text-ink-muted hover:bg-subtle">
              <Menu className="h-5 w-5" />
            </button>

            <h1 className="hidden lg:block text-sm font-bold text-ink truncate">{pageTitle}</h1>

            <div className="flex items-center gap-2 ml-auto">
              {/* Theme toggle */}
              <button
                onClick={toggleTheme}
                className="p-2 rounded-xl text-ink-dim hover:text-ink-muted hover:bg-subtle transition-all"
                aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
              </button>

              {/* Live indicator */}
              <div className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-subtle border border-line">
                <div className={`h-1.5 w-1.5 rounded-full ${isConnected ? 'bg-emerald-500 animate-pulse' : 'bg-ink-faint'}`} />
                <span className="text-xs text-ink-dim">{isConnected ? 'Live' : 'Offline'}</span>
              </div>

              {/* Notifications */}
              <div className="relative">
                <button onClick={() => {
                    // Opening the bell counts as seeing what's in it.
                    if (!notificationsOpen) markNotificationsRead();
                    setNotificationsOpen(!notificationsOpen);
                  }}
                  aria-label={unreadCount > 0
                    ? `Notifications (${unreadCount} unread)`
                    : 'Notifications'}
                  aria-expanded={notificationsOpen}
                  className="relative p-2 rounded-xl text-ink-dim hover:text-ink-muted hover:bg-subtle transition-all">
                  <Bell className="h-5 w-5" />
                  {unreadCount > 0 && (
                    <span aria-hidden="true"
                      className="absolute -top-0.5 -right-0.5 h-4 w-4 bg-accent text-accent-ink text-[10px] font-black rounded-full flex items-center justify-center">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>

                {notificationsOpen && (
                  <div className="absolute right-0 mt-2 w-80 app-panel app-border border rounded-2xl shadow-depth z-50 overflow-hidden animate-scale-in">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-line">
                      <h3 className="text-sm font-bold text-ink">Notifications</h3>
                      {notifications.length > 0 && (
                        <button onClick={clearNotifications} className="text-xs text-accent hover:brightness-110">Clear all</button>
                      )}
                    </div>
                    <div className="max-h-72 overflow-y-auto">
                      {notifications.length === 0 ? (
                        <div className="p-8 text-center">
                          <Bell className="h-8 w-8 mx-auto mb-2 text-ink-faint" />
                          <p className="text-sm text-ink-faint">All caught up</p>
                        </div>
                      ) : notifications.slice(0, 20).map((n, i) => {
                        // A follow notice opens the new follower's profile.
                        const href = n.type === 'follow' && n.from?.username ? `/users/${n.from.username}` : null;
                        const when = new Date(n.timestamp);
                        const sameDay = when.toDateString() === new Date().toDateString();
                        const body = (
                          <>
                            {n.from?.profilePicture ? (
                              <img src={n.from.profilePicture} alt="" className="h-8 w-8 rounded-full object-cover flex-shrink-0" />
                            ) : n.from ? (
                              <div className="h-8 w-8 rounded-full bg-accent/15 text-accent text-xs font-bold flex items-center justify-center flex-shrink-0">
                                {(n.from.name || n.from.username).slice(0, 1).toUpperCase()}
                              </div>
                            ) : null}
                            <div className="min-w-0">
                              <p className="text-sm text-ink-muted">{n.message}</p>
                              <p className="text-xs text-ink-faint mt-0.5">
                                {sameDay ? when.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : when.toLocaleDateString()}
                              </p>
                            </div>
                          </>
                        );
                        const cls = 'w-full flex items-center gap-3 px-4 py-3 border-b border-line hover:bg-subtle transition-colors text-left';
                        return href ? (
                          <button key={n.id ?? i} className={cls}
                            onClick={() => { setNotificationsOpen(false); navigate(href); }}>
                            {body}
                          </button>
                        ) : (
                          <div key={n.id ?? i} className={cls}>{body}</div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* pb-24 on mobile keeps the last row of content clear of the fixed
            bottom tab bar. */}
        <main id="main-content" className="flex-1 px-5 pt-6 pb-24 sm:px-8 sm:pt-8 lg:px-12 lg:pt-10 lg:pb-12">
          <Outlet />
        </main>
      </div>

      <MobileNav onOpenMenu={() => setSidebarOpen(true)} />

      <Heartbeat />
      {notificationsOpen && <div className="fixed inset-0 z-40" onClick={() => setNotificationsOpen(false)} />}
    </div>
  );
};

export default Layout;
